import { isPII } from './piiHelper';

/**
 * Utility functions for processing form questions, answers, and field data
 */

export const QuestionTrackingHelper = {
  /**
   * Check if a step represents an actual question
   * @param {Object} step - Step object with attributeid and nodetype properties
   * @returns {boolean} True if step is a question, false otherwise
   */
  isQuestion: (step) => {
    try {
      if (!step || typeof step !== 'object' || !step.attributeid) {
        return false;
      }

      // Exclude untracked step types that don't collect user input
      const UNTRACKED_STEP_TYPES = [
        'LabelNode', // Display labels
        'ProductSelection', // Product selection displays
        'process', // Processing steps
        'branch', // Logic branches
        'action', // Action steps
      ];

      if (UNTRACKED_STEP_TYPES.includes(step.nodetype)) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Extract field IDs from step array (questions only)
   * @param {Array} stepArray - Array of step objects with attributeid properties
   * @returns {Array} Array of question field IDs
   */
  extractFieldIds: (stepArray) => {
    try {
      if (!stepArray || !Array.isArray(stepArray)) {
        return [];
      }
      return stepArray
        .filter((step) => QuestionTrackingHelper.isQuestion(step))
        .map((step) => step.attributeid);
    } catch (error) {
      return [];
    }
  },

  /**
   * Process field values with PII masking
   * @param {Array} fieldIds - Array of field IDs
   * @param {Object} formData - Form data object
   * @returns {Array} Array of processed values (with PII masking)
   */
  processFieldValues: (fieldIds, formData) => {
    try {
      if (!fieldIds || !Array.isArray(fieldIds)) {
        return [];
      }
      if (!formData || typeof formData !== 'object') {
        return fieldIds.map(() => null);
      }

      return fieldIds.map((fieldId) => {
        const value = formData[fieldId];
        if (value === undefined || value === null || value === '') {
          return null;
        }
        return isPII(fieldId) ? 'pii-field' : value;
      });
    } catch (error) {
      return fieldIds ? fieldIds.map(() => null) : [];
    }
  },

  /**
   * Smart join with null handling logic
   * - If no questions: return null objects
   * - If single question with no answer: return null for answer
   * - If multiple questions: use pipe separator with "null" string for missing answers
   * @param {Array} fieldIds - Array of field IDs
   * @param {Array} values - Array of values
   * @returns {Object} Object with questions and answers strings
   */
  smartJoin: (fieldIds, values) => {
    try {
      // Handle null/undefined inputs
      if (!fieldIds || !Array.isArray(fieldIds) || fieldIds.length === 0) {
        return { questions: null, answers: null };
      }
      if (!values || !Array.isArray(values)) {
        return { questions: null, answers: null };
      }

      // Single question scenario
      if (fieldIds.length === 1) {
        const question = fieldIds[0];
        const answer = values[0];
        return {
          questions: question,
          answers: answer === null || answer === undefined ? null : answer,
        };
      }

      // Multiple questions scenario - use pipe separator
      const questions = fieldIds.join('|');
      const answers = values
        .map((value) =>
          value === null || value === undefined ? 'null' : value
        )
        .join('|');

      return { questions, answers };
    } catch (error) {
      return { questions: null, answers: null };
    }
  },

  /**
   * Build questions and answers from submitted props
   * @param {Object} submittedProps - Object with field names as keys and values
   * @returns {Object} Object with questions and answers strings (with smart null handling)
   */
  fromSubmittedProps: (submittedProps) => {
    try {
      // Handle null/undefined submittedProps
      if (!submittedProps || typeof submittedProps !== 'object') {
        return { questions: null, answers: null };
      }

      // submittedProps already contains only submitted form fields, not system properties
      const fieldIds = Object.keys(submittedProps);
      const values = QuestionTrackingHelper.processFieldValues(
        fieldIds,
        submittedProps
      );
      return QuestionTrackingHelper.smartJoin(fieldIds, values);
    } catch (error) {
      // Return safe defaults if anything fails
      return { questions: null, answers: null };
    }
  },

  /**
   * Build questions and answers from step array + form data
   * @param {Array} stepArray - Array of step objects
   * @param {Object} formData - Form data object (optional)
   * @returns {Object} Object with questions and answers strings (with smart null handling)
   */
  fromStepArray: (stepArray, formData = {}) => {
    try {
      // Handle null/undefined stepArray
      if (!stepArray || !Array.isArray(stepArray)) {
        return { questions: null, answers: null };
      }

      // Handle null formData
      const safeFormData = formData || {};

      const fieldIds = QuestionTrackingHelper.extractFieldIds(stepArray);
      const values = QuestionTrackingHelper.processFieldValues(
        fieldIds,
        safeFormData
      );
      return QuestionTrackingHelper.smartJoin(fieldIds, values);
    } catch (error) {
      // Return safe defaults if anything fails
      return { questions: null, answers: null };
    }
  },

  /**
   * Create defaulted fields data from field data
   * @param {Object} fieldData - Object with field names as keys and values
   * @returns {Array} Array of defaulted fields data objects
   */
  createDefaultedFieldsData: (fieldData) => {
    try {
      if (!fieldData || typeof fieldData !== 'object') {
        return [];
      }

      return Object.keys(fieldData).map((fieldName) => ({
        fieldEngaged: fieldName,
        fieldValue: isPII(fieldName) ? 'pii-field' : fieldData[fieldName],
      }));
    } catch (error) {
      return [];
    }
  },
};
